import { type ChangeEvent, useCallback, useId } from 'react';

import FormCheck from '@/components/elements/FormCheck';
import FormGroup from '@/components/elements/FormGroup';
import { useUIResourcePool } from '@/ducks/common/resources';
import { selectAccessible } from '@/ducks/filters/selectors';
import { setFilter } from '@/ducks/filters/setters';
import useOnKeyDown from '@/hooks/useOnKeyDown';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppSelector } from '@/store';

import './NewVoyageAccessibility.scss';

const UI_RESOURCE_POOL = {
  desktop: 'AccessibilityRefinement.CabinToolstip',
  mobile: 'AccessibilityRefinement.CabinToolstip2',
} as const;

const NewVoyageAccessibility = () => {
  const accessible = useAppSelector(selectAccessible);
  const { desktop, mobile } = useUIResourcePool(UI_RESOURCE_POOL);
  const { width } = useWindowSize();
  const id = useId();
  const formId = `NewVoyageAccessibility-${id}`;
  const formCheckId = `accesscabin-${id}`;

  const onKeyDownHandler = useCallback(() => {
    const toggledValue = !accessible;
    setFilter.accessible(toggledValue);
  }, [accessible]);

  // Native onChange event handler for mouse interaction
  const onCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFilter.accessible(checked);
  }, []);

  const onKeyDownInput = useOnKeyDown(onKeyDownHandler, { isDefaultPrevented: true });

  return (
    <div className="NewVoyageAccessibility">
      <form aria-label="Accessibility Option" id={formId}>
        <FormGroup tabIndex={-1}>
          <FormCheck
            aria-label={width! > 1200 ? desktop : mobile}
            checked={accessible}
            id={formCheckId}
            inputTabIndex="0"
            onChange={onCheckboxChange} // Use native onChange for mouse interaction
            onKeyDown={onKeyDownInput} // Use onKeyDownInput for keyboard interaction
            role="presentation"
            tabIndex="-1"
            type="checkbox"
          >
            <span className="inMobile">{mobile}</span>
            <span className="inDesktop">{desktop}</span>
          </FormCheck>
        </FormGroup>
      </form>
    </div>
  );
};

export default NewVoyageAccessibility;
